import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";
import { CVEFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/AssetSoftwareVulnerabilities/hooks/types";
import {
  CheckFindingsCheckNode,
  GenericFindingsNode,
} from "~/pages/inventory/asset/types";
import { Header } from "~/types/table";
import { getExceptionLink } from "~/pages/findings/Exceptions/utils";
import { AggregateScoresNode } from "~/components/FirewatchPage";
import { AssetVulnerabilitiesTableHeaders } from "~/pages/inventory/components/Findings/FindingsTable/hooks/useFindingsTable";
import { SelectedEntityWithFindingData } from "~/components/exceptions/SetExceptionDialog";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { GetExceptionEntitiesMrnsReturn } from "~/components/FirewatchPage/lib/getExceptionEntitiesMrns";
import { ExposedAsset } from "~/pages/space/vulnerabilities/components/exposed-assets/types";

export type FindingContext =
  | "advisory"
  | "cve"
  | "check"
  | "generic_finding"
  | "critical_exposure"
  // "all" context includes check, cve, advisory
  | "all";

const AdvisoryFindingsTableHeaders = (
  hasPermission: boolean,
  assetVulnerabilitiesTableHeaders: AssetVulnerabilitiesTableHeaders,
): Header[] => {
  return [
    ...(hasPermission ? [assetVulnerabilitiesTableHeaders.SELECT] : []),
    assetVulnerabilitiesTableHeaders.RISK_VALUE,
    assetVulnerabilitiesTableHeaders.ADVISORY_ID,
    assetVulnerabilitiesTableHeaders.RISKFACTORS,
  ].filter(Boolean) as Header[];
};

const VulnerabilitiesFindingsTableHeaders = (
  hasPermission: boolean,
  assetVulnerabilitiesTableHeaders: AssetVulnerabilitiesTableHeaders,
): Header[] => {
  return [
    ...(hasPermission ? [assetVulnerabilitiesTableHeaders.SELECT] : []),
    assetVulnerabilitiesTableHeaders.RISK_VALUE,
    assetVulnerabilitiesTableHeaders.VULNERABILITY_ID,
    assetVulnerabilitiesTableHeaders.RISKFACTORS,
  ].filter(Boolean) as Header[];
};

const CriticalExposuresFindingsTableHeaders = (
  hasPermission: boolean,
  assetVulnerabilitiesTableHeaders: AssetVulnerabilitiesTableHeaders,
  showAction: boolean,
): Header[] => {
  return [
    ...(hasPermission ? [assetVulnerabilitiesTableHeaders.SELECT] : []),
    assetVulnerabilitiesTableHeaders.RISK_VALUE,
    assetVulnerabilitiesTableHeaders.VULNERABILITY_ID,
    assetVulnerabilitiesTableHeaders.RISKFACTORS,
    ...(showAction ? [assetVulnerabilitiesTableHeaders.TRIAGE_BUTTON] : []),
  ].filter(Boolean) as Header[];
};

const ChecksFindingsTableHeaders = (
  hasPermission: boolean,
  assetVulnerabilitiesTableHeaders: AssetVulnerabilitiesTableHeaders,
): Header[] => {
  return [
    ...(hasPermission ? [assetVulnerabilitiesTableHeaders.SELECT] : []),
    assetVulnerabilitiesTableHeaders.RISK_VALUE,
    assetVulnerabilitiesTableHeaders.CHECK_ID,
    assetVulnerabilitiesTableHeaders.RISKFACTORS,
  ].filter(Boolean) as Header[];
};

const AllFindingsTableHeaders = (
  hasPermission: boolean,
  assetVulnerabilitiesTableHeaders: AssetVulnerabilitiesTableHeaders,
  showAction: boolean,
): Header[] => {
  return [
    ...(hasPermission ? [assetVulnerabilitiesTableHeaders.SELECT] : []),
    assetVulnerabilitiesTableHeaders.RISK_VALUE,
    assetVulnerabilitiesTableHeaders.AllSecurityFinding,
    assetVulnerabilitiesTableHeaders.RISKFACTORS,
    ...(showAction ? [assetVulnerabilitiesTableHeaders.TRIAGE_BUTTON] : []),
  ].filter(Boolean) as Header[];
};

export const getFindingTableHeaders = (
  context: FindingContext,
  hasPermission: boolean,
  assetVulnerabilitiesTableHeaders: AssetVulnerabilitiesTableHeaders,
  showAction: boolean,
): Header[] => {
  switch (context) {
    case "advisory":
      return AdvisoryFindingsTableHeaders(
        hasPermission,
        assetVulnerabilitiesTableHeaders,
      );
    case "cve":
      return VulnerabilitiesFindingsTableHeaders(
        hasPermission,
        assetVulnerabilitiesTableHeaders,
      );
    case "critical_exposure":
      return CriticalExposuresFindingsTableHeaders(
        hasPermission,
        assetVulnerabilitiesTableHeaders,
        showAction,
      );
    case "check":
      return ChecksFindingsTableHeaders(
        hasPermission,
        assetVulnerabilitiesTableHeaders,
      );
    case "all":
      return AllFindingsTableHeaders(
        hasPermission,
        assetVulnerabilitiesTableHeaders,
        showAction,
      );
    default:
      return [];
  }
};

interface FindingsTableRowHrefParams {
  context: FindingContext;
  assetId: string | undefined;
  jobId: string | undefined;
  projectId: string | undefined;
  scopeParams: URLSearchParams | undefined;
  finding:
    | CVEFindingsNode
    | AdvisoryFindingsNode
    | CheckFindingsCheckNode
    | GenericFindingsNode;
}

export const findingsTableRowHrefBuilder = ({
  context,
  assetId,
  jobId,
  projectId,
  scopeParams,
  finding,
}: FindingsTableRowHrefParams) => {
  const queryParams = new URLSearchParams(scopeParams);
  if (jobId && projectId) {
    queryParams.set("jobId", jobId);
    queryParams.set("projectId", projectId);
  }

  const _assetId = assetId || finding?.asset?.id;

  const buildUrl = (path: string, params: string = queryParams.toString()) => {
    return `/space/inventory/${_assetId}${path}?${params}`;
  };

  const urlMap = {
    advisory: () => buildUrl(`/advisories/${finding.id}`),
    cve: () => buildUrl(`/vulnerabilities/${finding.id}`),
    check: () =>
      buildUrl(
        `/check`,
        `checkMrn=${encodeURIComponent(finding.mrn)}&${queryParams}`,
      ),
    generic_finding: () =>
      buildUrl(
        `/generic-finding`,
        `genericFindingMrn=${encodeURIComponent(finding.mrn)}&${queryParams}`,
      ),
    critical_exposure: () => buildUrl(`/vulnerabilities/${finding.id}`),
    all: () => {
      switch (finding?.__typename) {
        case "AdvisoryFinding":
          return buildUrl(`/advisories/${finding.id}`);
        case "CveFinding":
          return buildUrl(`/vulnerabilities/${finding.id}`);
        case "CheckFinding":
          return buildUrl(
            `/check`,
            `checkMrn=${encodeURIComponent(finding.mrn)}&${queryParams}`,
          );
        case "GenericFinding":
          return buildUrl(
            `/generic-finding`,
            `genericFindingMrn=${encodeURIComponent(finding.mrn)}&${queryParams}`,
          );
        default:
          return "#";
      }
    },
  };

  return urlMap[context]?.() || "#";
};

interface FindingsTableRowExceptionHrefParams {
  id: string;
  exceptionId: string;
  assetId: string | undefined;
  scopeId: string | undefined;
  context: FindingContext;
}

export const findingTableRowExceptionHrefBuilder = ({
  id,
  exceptionId,
  assetId,
  scopeId,
  context,
}: FindingsTableRowExceptionHrefParams) => {
  switch (context) {
    case "critical_exposure":
      return getExceptionLink({
        id: exceptionId,
        queryParams: new URLSearchParams({ spaceId: scopeId || "" }),
      });

    default:
      return `/space/inventory/${assetId}/exceptions?spaceId=${scopeId}&exceptionId=${id}`;
  }
};

export const isAggregateScoresNode = (
  r:
    | CVEFindingsNode
    | AdvisoryFindingsNode
    | CheckFindingsCheckNode
    | AggregateScoresNode
    | ExposedAsset,
): r is AggregateScoresNode => {
  return (r as AggregateScoresNode).entity !== undefined;
};

export function sortFindingsToExceptionEntitiesMrns({
  selectedEntities,
}: {
  selectedEntities: Array<
    SelectedEntityWithFindingData & SelectedEntityWithException
  >;
}): GetExceptionEntitiesMrnsReturn {
  return selectedEntities.reduce<GetExceptionEntitiesMrnsReturn>(
    (finding, currentValue) => {
      switch (currentValue.type) {
        case "AdvisoryFinding":
          finding.advisoryMrns = finding.advisoryMrns?.length
            ? [...finding.advisoryMrns, currentValue.mrn]
            : [currentValue.mrn];
          finding.applyToCves = true;
          break;
        case "CveFinding":
          finding.cveMrns = finding.cveMrns?.length
            ? [...finding.cveMrns, currentValue.mrn]
            : [currentValue.mrn];
          break;
        case "GenericFinding":
          finding.fexMrns = finding.fexMrns?.length
            ? [...finding.fexMrns, currentValue.mrn]
            : [currentValue.mrn];
          break;
        case "CheckFinding":
        default:
          finding.queryMrns = finding.queryMrns?.length
            ? [...finding.queryMrns, currentValue.mrn]
            : [currentValue.mrn];
      }

      return finding;
    },
    {},
  );
}

export const getFindingMrn = (
  finding:
    | CVEFindingsNode
    | AdvisoryFindingsNode
    | CheckFindingsCheckNode
    | AggregateScoresNode
    | ExposedAsset,
) => {
  if (isAggregateScoresNode(finding)) {
    return finding.findingMrn || "";
  }

  return finding?.mrn || "";
};
